import { Controller } from "@hotwired/stimulus"
import { renderCounterValue } from "./counter_controller"

// Freezes one-time entry animations and counter values before Turbo
// caches the page. On back/forward navigation, the cached snapshot
// renders instantly with final values. On pull-to-refresh or fresh
// load, the server sends fresh HTML so everything plays normally.
export default class extends Controller {
  connect() {
    this._beforeCache = this._freezeForCache.bind(this)
    this._beforeRender = this._freezeIncomingIfReplacingPreview.bind(this)
    document.addEventListener("turbo:before-cache", this._beforeCache)
    document.addEventListener("turbo:before-render", this._beforeRender)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._beforeCache)
    document.removeEventListener("turbo:before-render", this._beforeRender)
  }

  _freezeForCache() {
    this._runFreezers(this.element)
    this._hideCelebrations(this.element)
  }

  // Which activation-arc popup belongs on screen is a server decision taken on
  // every render, so a snapshot must never paint one: the cached card is the
  // step the user was on last time, and it flashes for a frame before the
  // fresh render swaps in the current step's card.
  //
  // Hidden, not removed: these three come back on every render, and two of
  // them are reopened by tapping "Continue the setup", which needs them still
  // in the DOM.
  //
  // Deliberately NOT part of _runFreezers: that also runs over the incoming
  // server body, and hiding these there would suppress the very card the
  // server just decided to show.
  // .rate-ask is here for a sharper reason than the flash: its controller
  // reconnects on a restored snapshot and reruns its own show(), so a cached
  // copy does not merely paint, it re-raises the card and POSTs a second
  // showing — after the server has already retired it on review_asked_at.
  _hideCelebrations(root) {
    root.querySelectorAll(".welcome-cel, .streak-cel, .setup-prog, .rate-ask, .streak-up").forEach(el => {
      el.hidden = true
    })
  }

  // When Turbo is about to replace a preview with the fresh server render,
  // strip the animation triggers from the incoming body so the swap paints
  // in the same final state the preview was frozen in. Without this the
  // cached preview's frozen ring/counter/shimmer values would jump back
  // to initial frames for a moment before re-animating — a visible stutter.
  _freezeIncomingIfReplacingPreview(event) {
    if (!document.documentElement.hasAttribute("data-turbo-preview")) return
    const newBody = event.detail && event.detail.newBody
    if (!newBody) return
    this._runFreezers(newBody)
  }

  _runFreezers(root) {
    this._freezeAnimations(root)
    this._freezeCounters(root)
    this._freezeProgressBars(root)
    this._freezeReveals(root)
  }

  // The reveal controller hides its card on connect() and replays the
  // entrance choreography (count-ups, ring draws, bar grows) when it
  // scrolls into view. Cached snapshots must show the settled final state:
  // strip the controller so reconnect can't re-hide the card, and settle
  // the held child animations in their final frame.
  _freezeReveals(root) {
    root.querySelectorAll("[data-controller~='reveal']").forEach(el => {
      const controllers = el.dataset.controller
        .split(/\s+/)
        .filter(c => c !== "reveal")
        .join(" ")

      if (controllers) {
        el.dataset.controller = controllers
      } else {
        delete el.dataset.controller
      }

      el.classList.remove("reveal-pending", "reveal-in")

      el.querySelectorAll(".ring-anim, .bar-grow, .seg-grow, .trend-grow").forEach(child => {
        child.classList.remove("ring-anim", "bar-grow", "seg-grow", "trend-grow")
        child.style.animation = "none"
      })
    })
  }

  _freezeAnimations(root) {
    const animationClasses = [
      "dash-enter", "anim-up",
      "anim-d1", "anim-d2", "anim-d3", "anim-d4", "anim-d5",
      "badge-pop", "bubble-pop"
    ]

    const selector = animationClasses.map(c => `.${c}`).join(",")
    const inDocument = root.ownerDocument === document

    root.querySelectorAll(selector).forEach(el => {
      if (inDocument) {
        const computed = getComputedStyle(el)
        el.style.opacity = computed.opacity
        el.style.transform = computed.transform
      }

      animationClasses.forEach(cls => el.classList.remove(cls))
      el.style.animation = "none"
    })
  }

  _freezeCounters(root) {
    // Counter elements have data-controller="counter" and animate from
    // 0 to target on connect(). Removing the controller attribute keeps
    // the final text content and prevents re-animation from cache.
    root.querySelectorAll("[data-controller*='counter']").forEach(el => {
      // Write the final value first — an animated counter may still be at 0
      // (not yet scrolled into view) or mid count-up when the snapshot is
      // taken, and the fresh body replacing a preview always starts at 0.
      const target = parseFloat(el.dataset.counterTargetValue)
      if (!Number.isNaN(target)) {
        const precision = el.dataset.counterPrecisionValue !== undefined
          ? parseFloat(el.dataset.counterPrecisionValue)
          : 2
        renderCounterValue(el, target, precision)
      }

      const controllers = el.dataset.controller
        .split(/\s+/)
        .filter(c => c !== "counter")
        .join(" ")

      if (controllers) {
        el.dataset.controller = controllers
      } else {
        delete el.dataset.controller
      }

      // Clean up counter-specific data attributes
      delete el.dataset.counterTargetValue
      delete el.dataset.counterDurationValue
      delete el.dataset.counterPrecisionValue
      delete el.dataset.counterAnimateValue
    })
  }

  _freezeProgressBars(root) {
    root.querySelectorAll(".progress-animated").forEach(el => {
      // The final width from the bar's own --target-width, never a measured
      // one: the budget stage keeps every card but the active one
      // display:none, and a fill with no layout box measures 0px, which a
      // restored snapshot then keeps for good. The target is also the right
      // value for a fresh body replacing a preview, where nothing has been
      // laid out yet.
      const target = el.style.getPropertyValue("--target-width").trim()
      if (target) el.style.width = target
      el.style.animation = "none"
      el.classList.remove("progress-animated")
    })
  }
}
